import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { useLocation } from 'react-router-dom';

const EventsCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventType, setEventType] = useState('our'); // výchozí je "naše události"
  const language = getUserLanguage();
  const location = useLocation();
  const IMAGE_BASE_URL = 'https://www.norisknodrift.com';

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Při načtení stránky zkontrolujeme URL parametry
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeParam = params.get('type');
    if (typeParam && ['our', 'world'].includes(typeParam)) {
      console.log('[EventsCalendar] Setting event type from URL:', typeParam);
      setEventType(typeParam);
    }
  }, [location]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        console.log(`[EventsCalendar] Fetching ${eventType} events with language:`, language);
        const headers = { 'accept-language': language };
        const data = await get(`/events?type=${eventType}`, headers);
        console.log(`[EventsCalendar] ${eventType} events fetched:`, data.length);
        setEvents(data);
      } catch (error) {
        console.error('[EventsCalendar] Error fetching events:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst události' : 'Failed to load events data');
      } finally {
        setLoading(false);
      }
    };
    
    setLoading(true);
    fetchEvents();
  }, [language, eventType]);

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('[EventsCalendar] Invalid date string:', dateString);
      return language === 'cs' ? 'Neplatné datum' : 'Invalid date';
    }
    return date.toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US', options);
  };

  const toggleEventType = (type) => {
    if (type !== eventType) {
      console.log('[EventsCalendar] Changing event type to:', type);
      setEventType(type);
      const url = new URL(window.location);
      url.searchParams.set('type', type);
      window.history.pushState({}, '', url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Kompletní kalendář' : 'Full Calendar'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Kompletní kalendář' : 'Full Calendar'} />
        <div className="text-white text-center py-20">{error}</div>
        <Footer />
      </div>
    );
  }
  
  const descriptions = {
    our: {
      cs: 'Kompletní přehled všech závodů, exhibicí a drift zážitků s naším týmem. Přijďte nás podpořit nebo si užít nezapomenutelný driftový zážitek!',
      en: 'Complete overview of all races, exhibitions and drift experiences with our team. Come support us or enjoy an unforgettable drift experience!'
    },
    world: {
      cs: 'Přehled významných driftových a motoristických událostí po celém světě. Sledujte špičkové světové závody a události.',
      en: 'Overview of major drift and motorsport events around the world. Follow top world-class races and events.'
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'Kompletní kalendář' : 'Full Calendar'} />
      <section className="py-20 bg-black" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6">
          <p className="text-gray-400 max-w-3xl mx-auto text-center mb-10">
            {descriptions[eventType][language] || descriptions[eventType].en}
          </p>
          
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => toggleEventType('our')}
                className={`px-5 py-2 text-sm font-medium border border-red-600 rounded-l-lg ${
                  eventType === 'our'
                    ? 'bg-red-600 text-white'
                    : 'bg-transparent text-red-600 hover:bg-red-700 hover:text-white'
                }`}
              >
                {language === 'cs' ? 'Naše události' : 'Our Events'}
              </button>
              <button
                type="button"
                onClick={() => toggleEventType('world')}
                className={`px-5 py-2 text-sm font-medium border border-red-600 rounded-r-lg ${
                  eventType === 'world'
                    ? 'bg-red-600 text-white'
                    : 'bg-transparent text-red-600 hover:bg-red-700 hover:text-white'
                }`}
              >
                {language === 'cs' ? 'Události ve světě' : 'World Events'}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.length > 0 ? (
              events.map((event, index) => {
                const eventData = event.translations && event.translations[language]
                  ? event.translations[language]
                  : (event.translations && event.translations.en
                    ? event.translations.en
                    : { title: '', location: '', description: '' });
                
                // Kontrola, zda má událost platný obrázek (vylučujeme placeholder.jpg)
                const hasImage = event.image_path && 
                                event.image_path.trim() !== '' && 
                                !event.image_path.includes('placeholder.jpg');
                
                return (
                  <div
                    key={event.id}
                    className={`drift-card ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                    style={{ animationDelay: `${0.2 * index}s` }}
                  >
                    {hasImage ? (
                      <div className="relative h-48 overflow-hidden">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={`${IMAGE_BASE_URL}${event.image_path}`}
                            alt={eventData.title || 'Event'}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              console.error('[EventsCalendar] Image load error for event:', event.id, event.image_path);
                              // Přepneme na zobrazení bez obrázku - nahrazujeme celý div s obrázkem
                              e.target.parentNode.parentNode.outerHTML = `
                                <div class="bg-gray-800 p-4 rounded-t-lg flex justify-between items-center">
                                  <div>
                                    ${event.event_type === 'world' ? `
                                      <span class="bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded">
                                        ${language === 'cs' ? 'SVĚTOVÁ UDÁLOST' : 'WORLD EVENT'}
                                      </span>
                                    ` : ''}
                                  </div>
                                  <div class="bg-red-600 text-white text-sm font-semibold px-3 py-1 rounded">
                                    ${formatDate(event.date)}
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        </div>
                        <div className="absolute top-0 right-0 bg-red-600 text-white text-sm font-semibold px-3 py-1 z-10">
                          {formatDate(event.date)}
                        </div>
                        {event.event_type === 'world' && (
                          <div className="absolute top-0 left-0 bg-purple-600 text-white text-xs font-semibold px-2 py-1 z-10">
                            {language === 'cs' ? 'SVĚTOVÁ UDÁLOST' : 'WORLD EVENT'}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-800 p-4 rounded-t-lg flex justify-between items-center">
                        <div>
                          {event.event_type === 'world' && (
                            <span className="bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded">
                              {language === 'cs' ? 'SVĚTOVÁ UDÁLOST' : 'WORLD EVENT'}
                            </span>
                          )}
                        </div>
                        <div className="bg-red-600 text-white text-sm font-semibold px-3 py-1 rounded">
                          {formatDate(event.date)}
                        </div>
                      </div>
                    )}
                    
                    <div className="p-6">
                      <h3 className="text-xl font-title font-bold text-white mb-2">
                        {eventData.title || (language === 'cs' ? 'Bezejmenná událost' : 'Unnamed Event')}
                      </h3>
                      <div className="flex items-center text-gray-400 text-sm mb-4">
                        <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
                        <span>{eventData.location || (language === 'cs' ? 'Není uvedeno místo' : 'Location not specified')}</span>
                      </div>
                      <p className="text-gray-400 text-sm mb-4">
                        {eventData.description || (language === 'cs' ? 'Žádný popis' : 'No description')}
                      </p>
                      <a href={event.event_url || '#'} className="btn-drift text-xs">
                        <i className="fas fa-info-circle mr-1"></i>
                        {language === 'cs' ? 'Více informací' : 'More Info'}
                      </a>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 text-center text-gray-400 py-10">
                {eventType === 'our' 
                  ? (language === 'cs' ? 'Momentálně nejsou žádné události našeho týmu.' : 'Currently no events of our team.')
                  : (language === 'cs' ? 'Momentálně nejsou žádné světové události.' : 'Currently no world events.')}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EventsCalendar;